/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import useBaseService from '../../../hooks/services/useBaseService'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import { entities } from '../../../constants/entities'
import MainLayout from '../../../layouts/MainLayout'
import PageHeader from '../../../components/PageHeader'
import { faFileExcel, faFilePdf } from '@fortawesome/free-solid-svg-icons'
import useForm from '../../../hooks/useForm'
import { setDefaultFieldValues } from '../../../utils/objectUtils'
import SimpleBody from '../../../components/forms/SimpleBody'
import SectionHeader from '../../../components/forms/SectionHeader'
import Form from '../../../components/forms/Form'
import { filterFields } from './filters'
import useRemoteSelectOptions from '../../../hooks/useRemoteSelectOptions'
import Button from '../../../components/Button'
import { Alert, Box } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useOrgan } from '../../../hooks/useOrgan'
import useSelectOptions from '../../../hooks/useSelectOptions'
import { useReport } from '../../../context/ReportContext'

const ContractsReport = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedOrgan } = useOrgan()
  const { setExportId, isExporting } = useReport()

  const [filtersSection] = filterFields
  const { formData, handleChange, setFormData } = useForm(
    setDefaultFieldValues([filtersSection]),
    [filtersSection]
  )

  const { get: baseServiceGet } = useBaseService(navigate)
  const { hideLoader, showLoader } = useLoader()
  const { showNotification } = useNotification()

  // Modo do relatório (detalhado com itens ou simplificado)
  const modeSelectOptions = useSelectOptions({
    fieldId: 'filter.mode',
    formData,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
    setFormData,
    rawOptions: [
      { value: 'detailed', label: 'Detalhado (com itens)' },
      { value: 'simplified', label: 'Simplificado' },
    ],
  })

  // Cliente contratante
  const customerSelectOptions = useRemoteSelectOptions({
    queryKey: ['customers'],
    entityPath: entities.customers.contractants.get,
    baseServiceGet,
    fieldId: 'filter.customer_id',
    formData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.alias}`,
    }),
  })

  const customerSelectedRef = useRef(customerSelectOptions.selectedValue?.value)

  // Contrato (opcional, filtrado pelo cliente contratante e organização)
  const contractSelectOptions = useRemoteSelectOptions({
    queryKey: ['contractsByCustomer'],
    entityPath: entities.contracts.get,
    baseServiceGet,
    extraFilters: {
      customer_id: customerSelectOptions.selectedValue?.value,
      organization_id: selectedOrgan?.id,
    },
    defaultSort: { column: 'id', order: 'desc' },
    fieldId: 'filter.contract_id',
    formData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchWhen: () => {
      // Mudar quando o cliente selecionado mudar
      if (
        customerSelectedRef.current !==
        customerSelectOptions.selectedValue?.value
      ) {
        customerSelectedRef.current = customerSelectOptions.selectedValue?.value
        contractSelectOptions.reset()
        return true
      }

      return false
    },
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.number} - ${item.name}`,
    }),
  })

  // Somente vencidos?
  const onlyExpiredSelectOptions = useSelectOptions({
    fieldId: 'filter.only_expired',
    formData,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
    setFormData,
    rawOptions: [
      { value: true, label: 'Sim' },
      { value: false, label: 'Não' },
    ],
  })

  // Somente com excedentes?
  const withExcessSelectOptions = useSelectOptions({
    fieldId: 'filter.with_excess',
    formData,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
    setFormData,
    rawOptions: [
      { value: true, label: 'Sim' },
      { value: false, label: 'Não' },
    ],
  })

  useEffect(() => {
    // Por padrão: detalhado, sem somente vencidos e sem somente excedentes
    setFormData((prev) => ({
      filter: {
        ...prev.filter,
        mode: { value: 'detailed', label: 'Detalhado (com itens)' },
        only_expired: { value: false, label: 'Não' },
        with_excess: { value: false, label: 'Não' },
      },
    }))
  }, [])

  const selects = {
    'filter.mode': modeSelectOptions,
    'filter.customer_id': customerSelectOptions,
    'filter.contract_id': contractSelectOptions,
    'filter.only_expired': onlyExpiredSelectOptions,
    'filter.with_excess': withExcessSelectOptions,
  }

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [selects]
  )

  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.options || []
    },
    [selects]
  )

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.selectedValue || null
    },
    [selects]
  )

  const exportReport = useMutation({
    mutationFn: async (type) => {
      showLoader()

      const detailed = formData.filter.mode?.value !== 'simplified'

      const params = {
        filters: {
          customer_id: formData.filter.customer_id?.value ?? null,
          contract_id: formData.filter.contract_id?.value ?? null,
          only_expired: !!formData.filter.only_expired?.value,
          with_excess: !!formData.filter.with_excess?.value,
          organization_id: selectedOrgan?.id,
        },
      }

      const response = await baseServiceGet(
        entities.reports.contracts.get,
        params,
        { type, detailed }
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 202 || response?.status === 200) {
        const generated_id = response.result?.export_id

        if (generated_id) {
          setExportId(generated_id)
          localStorage.setItem('report_exportId', generated_id.toString())
          showNotification(
            'info',
            'Processamento do relatório iniciado em segundo plano.'
          )
        }
      }
    },
    onError: (error) => {
      console.error('onError ', error)
      hideLoader()
    },
    onSettled: () => hideLoader(),
  })

  useEffect(() => {
    if (location.state?.message) {
      showNotification('error', location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title="Relatório de Contratos" showBackButton={false} />
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Atenção:</strong> Este relatório gera a lista de contratos com
        filtros por cliente contratante, vencidos e com excedentes, nos modos
        detalhado (com os itens do contrato) ou simplificado.
      </Alert>
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={() => {}}
          textSubmit="Filtrar"
          textLoadingSubmit="Filtrando..."
          showButtons={false}
          disabledSubmitButton={true}>
          {() => (
            <>
              <div key={filtersSection.section}>
                <SectionHeader section={filtersSection.section} />
                <SimpleBody
                  fields={filtersSection.fields}
                  formErrors={{}}
                  formData={formData}
                  handleFieldChange={handleChange}
                  getOptions={getOptions}
                  getSelectedValue={getSelectedValue}
                  getSelectMeta={getSelectMeta}
                />
              </div>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  my: 4,
                  width: '100%',
                }}>
                <Button
                  variant="outlined"
                  color="primary"
                  type="button"
                  startIcon={<FontAwesomeIcon icon={faFilePdf} />}
                  text="Exportar em PDF"
                  disabled={isExporting}
                  onClick={async () => {
                    try {
                      await exportReport.mutateAsync('pdf')
                    } catch (error) {
                      console.error('Erro ao exportar PDF:', error)
                    }
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  type="button"
                  startIcon={<FontAwesomeIcon icon={faFileExcel} />}
                  text="Exportar em EXCEL"
                  disabled={isExporting}
                  onClick={async () => {
                    try {
                      await exportReport.mutateAsync('excel')
                    } catch (error) {
                      console.error('Erro ao exportar EXCEL:', error)
                    }
                  }}
                />
              </Box>
            </>
          )}
        </Form>
      </div>
    </MainLayout>
  )
}

export default ContractsReport
